<template>
  <FormKit
    type="form"
    id="theFormId"
    :value="amendmentRequest"
    v-model="amendmentRequest"
    @submit="handleSubmit"
    :actions="false"
    autocomplete="off"
  >
    <template #default="{ state: formState }">
      <div class="pb-5 sm:flex-1 sm:overflow-auto">
        <FormSection v-if="amendmentRequest">
          <template #sectionName>Amendment Request Details</template>
          <template #sectionDescription>
            <p>If you need to modify your distributor report, request an amendment.</p>
            <br />
            <p>You will be notified after your request is reviewed and your distributor report can be modified.</p>
          </template>
          <template #default>
            <FormKit
              label="Amendment Reason"
              name="amendmentReason"
              type="textarea"
              validation="required:trim|length:0,255"
            />
          </template>
        </FormSection>
      </div>
      <FormActions
        :save-action="saveAction"
        :cancel-action="cancelAction"
        :form-state="formState"
        save-label="Submit"
      ></FormActions
    ></template>
  </FormKit>
</template>

<script lang="ts">
import { routeNames } from "@/routes";
import {
  type AmendmentRequest,
  type AmendmentRequestFormKit,
  type AmendmentRequestResponse,
} from "@/views/amendmentRequest/amendmentRequest";
import { createAmendmentRequest } from "@/views/amendmentRequest/amendmentRequestApi";
import { FormKit, submitForm } from "@formkit/vue";
import { FormActions, FormSection } from "@modernary/mui/components/form";
import { apiSaveErrorHandler, useApi } from "@modernary/mui/utils/api";
import { routeCancelAction } from "@modernary/mui/utils/helpers";
import { defineComponent, ref, type Ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { getDistributorReportById } from "../distributorReport/distributorReportApi";
import distributorReportService from "../distributorReport/distributorReportService";
import { fillLookupValuesByPropertyNames } from "@modernary/mui/components/lookup";
import { LE } from "@/constants/enums";

export default defineComponent({
  name: "AmendmentRequest",
  components: {
    FormSection,
    FormActions,
  },
  setup(props) {
    const router = useRouter();

    const route = useRoute();
    const distributorReportId = route.params.id;

    const amendmentRequest = ref<AmendmentRequest>();

    const { exec: fetchDistributorReport } = useApi(getDistributorReportById, {
      responseAdapter: async (response) => {
        await distributorReportService.skinnyResponseAdapter(response);
        await fillLookupValuesByPropertyNames(response.data, ["periodMurn"], { replaceAll: true });
        return response.data;
      },
    });

    const { exec: execCreateAmendmentRequest, isStatusPending } = useApi<AmendmentRequest, AmendmentRequestResponse>(
      createAmendmentRequest,
      {
        responseAdapter: async (response) => {
          // Things get awkward when our API returns a very different type than we're creating.
          const result = response.data as unknown as AmendmentRequestResponse;
          if (result.wasRequestSubmitted) {
            cancelAction();
          }

          return result;
        },
      },
    );

    const handleSubmit = async (data: AmendmentRequest) => {
      let result: AmendmentRequest | undefined;
      return apiSaveErrorHandler(
        async () => {
          await fetchDistributorReport(distributorReportId).then(async (result) => {
            data.distributorReportId = result?.id;
            data.distributorName = result?.distributorName;
            data.reportPeriod = `${result?.frequencyMurn === LE.ReportFrequency.SemiAnnual ? "CY " : ""}${result?.year} ${result?.period?.description}`;
            data.distributorId = result?.distributorId;
            data.statusMurn = result?.statusMurn;
            data.submissionNumber = result?.submissionNumber;
            delete data["id"];
            delete data["murn"];
          });
        },
        async () => {
          return await execCreateAmendmentRequest(data);
        },
        async () => {
          if (!result) return;
        },
      );
    };

    const saveAction = function () {
      return submitForm("theFormId");
    };

    const cancelAction = function () {
      routeCancelAction(router, routeNames.home);
    };

    return {
      handleSubmit,
      saveAction,
      cancelAction,
      amendmentRequest: amendmentRequest as Ref<AmendmentRequestFormKit>,
    };
  },
});
</script>
